import { CardErrorState } from './CardErrorState';
import { InfoTooltip } from './InfoTooltip';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

/**
 * `.pen` → `ds/chart/donut` (`ws9qc`) — **um só master parametrizável**.
 *
 * Substitui as 7 implementações separadas de donut que existiam no React
 * (dashboard, património, contas, despesas, rendimentos, relatório). Os três
 * interruptores do master são props aqui:
 *
 *  - `center`  → `Sw6KS` (valor central, `enabled:false` por defeito)
 *  - `role` por fatia → `rN-role` (papel descritivo, `enabled:false` por defeito)
 *  - `foot`    → `llSLq` (rodapé de 2 métricas)
 *
 * Mais os estados `empty` (`AyQC5`), `error` (`bR9sN`) e `loading` (`i5KUY`).
 *
 * Geometria do anel (Get no master, `resolveVariables:true`):
 *   ring 160×160 · `innerRadius` 0.775 → traço 18px, raio 71
 *   folga de 2,4° entre fatias · início a 90° (12h), sentido horário
 *   track `$track` · até 6 fatias (`seg-1..6`) e 6 linhas (`row-1..6`)
 *
 * Casca: `rounded-ds-lg border border-border bg-card p-space-5 gap-space-5`.
 * `variant="plain"` remove só a casca (relatório de selfie, onde o cartão já
 * é o `ReportPanel` à volta) — estrutura e tipografia mantêm-se.
 */

const RING_PX = 160;
const RING_RADIUS = 71;
const RING_STROKE = 18;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const SLICE_GAP_DEG = 2.4;
const MAX_SLICES = 6;

/** `.pen` `rN-dot-box` — caixa de alinhamento do ponto com a 1ª linha do nome. */
const DOT_BOX_STYLE = { height: 19 };
/** `.pen` `rN-num` — coluna numérica de largura fixa. */
const NUM_COL_STYLE = { width: 92 };
/** `.pen` `sk-bar` / `sk-num` — barras do esqueleto. */
const SK_BAR_STYLE = { height: 11 };

const BODY_LAYOUT_CLASS = {
  stacked: '',
  split: 'flex-row items-center',
  auto: 'md:flex-row md:items-center',
  /** Coluna estreita do Início: volta a empilhar quando a grelha reparte em `lg`. */
  'auto-narrow': 'md:flex-row md:items-center lg:flex-col lg:items-stretch',
};

function toPercent(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

/** Anel SVG — track + fatias, cada uma rodada para o seu ângulo de início. */
function DonutRing({ slices, total, className }) {
  let cursor = 0;
  const multi = slices.length > 1;

  return (
    <svg
      width={RING_PX}
      height={RING_PX}
      viewBox={`0 0 ${RING_PX} ${RING_PX}`}
      className={className}
      aria-hidden
    >
      <circle
        cx={RING_PX / 2}
        cy={RING_PX / 2}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={RING_STROKE}
        className="stroke-track"
      />
      {slices.map((slice) => {
        const fraction = total > 0 ? slice.value / total : 0;
        const startDeg = -90 + cursor * 360;
        const sweepDeg = Math.max(
          0,
          fraction * 360 - (multi ? SLICE_GAP_DEG : 0),
        );
        cursor += fraction;
        if (sweepDeg <= 0) return null;
        return (
          <circle
            key={slice.id}
            cx={RING_PX / 2}
            cy={RING_PX / 2}
            r={RING_RADIUS}
            fill="none"
            stroke={slice.color}
            strokeWidth={RING_STROKE}
            strokeDasharray={`${(sweepDeg / 360) * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`}
            transform={`rotate(${startDeg} ${RING_PX / 2} ${RING_PX / 2})`}
          />
        );
      })}
    </svg>
  );
}

/** `.pen` `empty` — anel de contorno com a copy centrada, mesma altura do anel. */
function DonutEmpty({ label }) {
  return (
    <div className="flex w-full items-center justify-center">
      <div className="relative flex h-40 w-40 items-center justify-center rounded-ds-full border-2 border-border">
        <p className="type-caption w-28 text-center text-muted-foreground">
          {label}
        </p>
      </div>
    </div>
  );
}

/** `.pen` `loading` — anel esqueleto + 4 linhas de legenda. */
function DonutLoading() {
  return (
    <div
      className="flex w-full flex-col items-center gap-space-5"
      aria-busy="true"
    >
      <svg
        width={RING_PX}
        height={RING_PX}
        viewBox={`0 0 ${RING_PX} ${RING_PX}`}
        className="shrink-0"
        aria-hidden
      >
        <circle
          cx={RING_PX / 2}
          cy={RING_PX / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE}
          className="stroke-skeleton"
        />
      </svg>
      <div className="flex w-full flex-col gap-space-3">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="flex items-center gap-space-3">
            <span className="h-2 w-2 shrink-0 rounded-ds-full bg-skeleton" />
            <span
              className="flex-1 rounded-ds-full bg-skeleton"
              style={SK_BAR_STYLE}
            />
            <span
              className="w-16 shrink-0 rounded-ds-full bg-skeleton"
              style={SK_BAR_STYLE}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DonutChart({
  title,
  subtitle,
  infoTooltip,
  slices = [],
  center = null,
  foot = null,
  showLegend = true,
  bodyLayout = 'auto',
  state = 'default',
  emptyLabel,
  errorLabel,
  onRetry,
  valueFormatter = formatEUR,
  variant = 'card',
  className,
  ...rest
}) {
  const rows = slices.slice(0, MAX_SLICES);
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const hasHead = Boolean(title || subtitle);

  const shellClass = cn(
    'flex w-full min-w-0 flex-col gap-space-5',
    variant === 'card'
      && 'rounded-ds-lg border border-border bg-card p-space-5 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]',
    className,
  );

  const head = hasHead ? (
    <div className="flex justify-between gap-space-3">
      <div className="flex min-w-0 flex-1 flex-col gap-space-1">
        {title ? (
          <p className="type-section text-muted-foreground">{title}</p>
        ) : null}
        {subtitle ? (
          <p className="type-body-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {infoTooltip ? (
        <InfoTooltip text={infoTooltip} className="h-3.5 w-3.5 shrink-0" />
      ) : null}
    </div>
  ) : null;

  if (state === 'loading') {
    return (
      <section className={shellClass} {...rest}>
        {head}
        <DonutLoading />
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section className={shellClass} {...rest}>
        {head}
        <CardErrorState
          title={errorLabel || pt.common.charts.distributionError}
          onRetry={onRetry}
          className="h-40 justify-center"
        />
      </section>
    );
  }

  if (state === 'empty' || !rows.length) {
    return (
      <section className={shellClass} {...rest}>
        {head}
        <DonutEmpty label={emptyLabel || pt.common.charts.distributionEmpty} />
      </section>
    );
  }

  return (
    <section className={shellClass} {...rest}>
      {head}

      <div
        className={cn(
          'flex flex-col items-center gap-space-5',
          BODY_LAYOUT_CLASS[bodyLayout] ?? BODY_LAYOUT_CLASS.auto,
        )}
      >
        <div className="relative h-40 w-40 shrink-0">
          <DonutRing slices={rows} total={total} className="h-40 w-40" />
          {center ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-center">
              {center.label ? (
                <span className="type-section text-muted-foreground">
                  {center.label}
                </span>
              ) : null}
              <span className="type-display-metric text-foreground">
                {center.value}
              </span>
            </div>
          ) : null}
        </div>

        {showLegend ? (
          <ul className="flex w-full min-w-0 flex-col gap-space-3">
            {rows.map((row) => (
              <li key={row.id} className="flex min-w-0 gap-space-3">
                <span
                  className="flex w-2 shrink-0 items-center"
                  style={DOT_BOX_STYLE}
                >
                  <span
                    className="h-2 w-2 rounded-ds-full"
                    style={{ backgroundColor: row.color }}
                    aria-hidden
                  />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="type-body truncate text-foreground">
                    {row.name}
                  </span>
                  {row.role ? (
                    <span className="type-caption text-muted-foreground">
                      {row.role}
                    </span>
                  ) : null}
                </span>
                <span
                  className="flex shrink-0 flex-col items-end gap-0.5"
                  style={NUM_COL_STYLE}
                >
                  <span className="type-display-row text-right text-foreground">
                    {valueFormatter(row.value)}
                  </span>
                  <span className="type-label-meta text-right text-muted-foreground">
                    {row.percent != null
                      ? Math.round(row.percent)
                      : toPercent(row.value, total)}
                    %
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {foot?.length ? (
        <div className="flex items-end gap-space-4 border-t border-border pt-space-4">
          {foot.map((metric) => (
            <div
              key={metric.label}
              className="flex min-w-0 flex-1 flex-col gap-0.5"
            >
              <span className="type-section text-muted-foreground">
                {metric.label}
              </span>
              <span className="type-display-stat text-foreground">
                {metric.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default DonutChart;
